/**
 * How the panel looks, chosen per device in Configuración:
 * - Dale (the default): the layout of the CRM designed for Helios (top bar, the
 *   business in the sidebar, bordered surfaces, clients as cards) dressed in
 *   Dale's colors. Martín's rule since 2026-09-26: the default is never recolored.
 * - Moderno: the CRM designed for Helios as drawn, its navy, cream and editorial type.
 * - Futurístico: Dale behind glass. Translucent layers on a canvas of Dale's light,
 *   edges that catch it and soft depth; Dale's layout, colors and type underneath.
 * - Vintage: Dale as it was.
 * Nothing here changes what the panel does.
 */

export const LOOKS = ['dale', 'moderno', 'futuristico', 'vintage'] as const;
export type Look = (typeof LOOKS)[number];

export const DEFAULT_LOOK: Look = 'dale';
/** A new key since 2026-10-07: under the earlier one the same names meant other looks. */
export const LOOK_STORAGE_KEY = 'dale.crm.look.v2';
const PREVIOUS_LOOK_STORAGE_KEY = 'dale.crm.look';

export const LOOK_LABEL: Readonly<Record<Look, string>> = {
  dale: 'Dale',
  moderno: 'Moderno',
  futuristico: 'Futurístico',
  vintage: 'Vintage',
};

export const LOOK_DESCRIPTION: Readonly<Record<Look, string>> = {
  dale: 'El diseño de Helios con los colores de Dale.',
  moderno: 'El diseño de Helios: azul noche, crema y letra editorial.',
  futuristico: 'Vidrio sobre los colores de Dale: capas translúcidas y luz en los bordes.',
  vintage: 'Dale como era al principio.',
};

/**
 * The tokens on <html data-look>: "helios" is the layout (in Dale's colors on its
 * own), "paleta-helios" the Helios navy, cream and type, "vidrio" the glass. Since
 * 2026-10-07 Futurístico is the Dale look behind glass, so it never takes the
 * Helios palette: Martín asked that the glass keep Dale's colors always.
 */
const LOOK_ATTRIBUTE: Readonly<Record<Look, string>> = {
  dale: 'helios',
  moderno: 'helios paleta-helios',
  futuristico: 'helios vidrio',
  vintage: 'clasico',
};

/**
 * What a device saved under the earlier key, by what each name showed then:
 * Moderno was the Helios layout in Dale's colors (now Dale) and Futurístico the
 * Helios design (now Moderno). "helios", "clasico" and the retired "suave" are older still.
 */
const PREVIOUS_LOOK: ReadonlyMap<string, Look> = new Map([
  ['moderno', 'dale'],
  ['futuristico', 'moderno'],
  ['vintage', 'vintage'],
  ['helios', 'dale'],
  ['clasico', 'vintage'],
  ['suave', 'dale'],
]);

type LookStorage = Pick<Storage, 'getItem' | 'setItem'>;

export function lookAttribute(look: Look): string {
  return LOOK_ATTRIBUTE[look];
}

export function isLook(value: unknown): value is Look {
  return typeof value === 'string' && (LOOKS as readonly string[]).includes(value);
}

/**
 * The saved choice, else what the device saw before the looks were reassigned,
 * else Dale. Blocked or cleared storage never breaks the panel.
 */
export function readLook(storage: LookStorage | undefined): Look {
  try {
    const saved = storage?.getItem(LOOK_STORAGE_KEY) ?? null;
    if (isLook(saved)) return saved;
    return PREVIOUS_LOOK.get(storage?.getItem(PREVIOUS_LOOK_STORAGE_KEY) ?? '') ?? DEFAULT_LOOK;
  } catch {
    return DEFAULT_LOOK;
  }
}

/** The look a link asks for (`?look=futuristico`), so a demo or a shared link opens dressed. */
export function lookFromSearch(search: string): Look | undefined {
  const asked = new URLSearchParams(search).get('look');
  return isLook(asked) ? asked : undefined;
}

export function saveLook(storage: LookStorage | undefined, look: Look): void {
  try {
    storage?.setItem(LOOK_STORAGE_KEY, look);
  } catch {
    // Private windows and blocked site data: the choice lasts only this visit.
  }
}
